import { useState } from 'react';
import CredentialCreateDialog from './CredentialCreateDialog';
import { useAuth } from '@/hooks/useAuth';
import { credentialTypeLabel, useCredentials, type Credential } from '@/hooks/useCredentials';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Item values that are not credential ids.
const NONE = '__no_credential__';
const CREATE = '__create_credential__';
const OWN_NEW = '__own_new_credential__';

interface CredentialSelectProps {
  /** For the field's `<Label htmlFor>`. */
  id?: string;
  /** The chosen credential's id; null for none. */
  value: string | null;
  onChange: (credentialId: string | null) => void;
  /**
   * The region of what the credential is for. Only its credentials are listed,
   * because a credential's secret lives only on its own region's gateways, and a
   * new one is created there. Empty while the caller does not know it yet: the
   * select waits for it.
   */
  regionId: string;
  /**
   * The division of what the credential is for, where that limits the choice:
   * org-level credentials and that division's are listed (`null`: org-level
   * only). Left out, divisions play no part.
   */
  divisionId?: string | null;
  /** The types the use takes (`credentialTypes`). */
  types: readonly Credential['type'][];
  /** Offer "None", which chooses null. */
  optional?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /**
   * The caller's own way to make a new credential: an item labelled `label`,
   * shown as the choice while `selected`, that calls `onSelect`. The caller
   * renders whatever it needs beside the select, and creates the credential
   * itself. It stands in for "+ New credential…" and its dialog.
   */
  ownNew?: { label: string; selected: boolean; onSelect: () => void };
  /** Steps the "+ New credential…" dialog shows above a type's fields. */
  guidance?: Partial<Record<Credential['type'], React.ReactNode>>;
}

/**
 * The one control for choosing a credential. Every form that takes one uses it,
 * so they all list the same credentials (the region's, of the types the use
 * takes), label them alike, and offer to create one in place.
 */
export default function CredentialSelect({
  id,
  value,
  onChange,
  regionId,
  divisionId,
  types,
  optional = false,
  placeholder = 'Select a credential',
  disabled = false,
  ownNew,
  guidance,
}: CredentialSelectProps) {
  const { user } = useAuth();
  const { data: credentials } = useCredentials(user?.organizationId);
  const [creating, setCreating] = useState(false);

  const listed = (credentials ?? []).filter(
    (c) =>
      (types.includes(c.type) &&
        c.regionId === regionId &&
        (divisionId === undefined || c.divisionId === null || c.divisionId === divisionId)) ||
      // The current choice stays visible even when it no longer fits (saved
      // before these rules, say), rather than reading as no choice at all.
      c.id === value,
  );

  const selected = ownNew?.selected ? OWN_NEW : (value ?? (optional ? NONE : ''));

  return (
    <>
      <Select
        value={selected}
        disabled={disabled || !regionId}
        onValueChange={(v) => {
          if (v === CREATE) { setCreating(true); }
          else if (v === OWN_NEW) { ownNew?.onSelect(); }
          else if (v === NONE) { onChange(null); }
          // Radix sends '' after an item the handler kept from becoming the
          // value; '' is never a choice.
          else if (v) { onChange(v); }
        }}
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder={regionId ? placeholder : 'Choose a region first'} />
        </SelectTrigger>
        <SelectContent>
          {optional ? <SelectItem value={NONE}>None</SelectItem> : null}
          {listed.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name} ({credentialTypeLabel(c.type)})
            </SelectItem>
          ))}
          {ownNew ? (
            <SelectItem value={OWN_NEW}>{ownNew.label}</SelectItem>
          ) : (
            <SelectItem value={CREATE}>+ New credential…</SelectItem>
          )}
        </SelectContent>
      </Select>
      {ownNew ? null : (
        <CredentialCreateDialog
          open={creating}
          onOpenChange={setCreating}
          regionId={regionId}
          allowedTypes={[...types]}
          guidance={guidance}
          onCreated={(created) => { onChange(created); }}
        />
      )}
    </>
  );
}
